import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
} from 'firebase/firestore';

// ─── Replace with your Firebase project config ───────────────────────────────
// Get this from: Firebase Console → Project Settings → Your apps → Web app
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
// ─────────────────────────────────────────────────────────────────────────────

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);

// ─── Lead helpers ─────────────────────────────────────────────────────────────

// Normalise a business name into a stable ID (used for dedup)
export function slugify(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Merge two lead objects — existing wins unless new field has value and existing doesn't
function mergeLeads(existing, incoming) {
  const merged = { ...existing };
  for (const [key, val] of Object.entries(incoming)) {
    if (val !== null && val !== undefined && val !== '') {
      if (!merged[key] || merged[key] === '') {
        merged[key] = val;
      }
    }
  }
  merged.updatedAt = serverTimestamp();
  return merged;
}

// Upsert a lead — deduplicates by slugified business name
// Returns { action: 'created' | 'merged' | 'skipped', id }
export async function upsertLead(leadData) {
  if (!leadData.businessName) throw new Error('businessName is required');

  const id = slugify(leadData.businessName);
  const ref = doc(db, 'leads', id);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    await setDoc(ref, {
      ...leadData,
      id,
      status: leadData.status || 'new',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { action: 'created', id };
  }

  const existing = snap.data();
  const merged = mergeLeads(existing, leadData);
  const changed = JSON.stringify(existing) !== JSON.stringify(merged);

  if (!changed) return { action: 'skipped', id };

  await updateDoc(ref, merged);
  return { action: 'merged', id };
}

// Fetch all leads, newest first
export async function getLeads(filters = {}) {
  let q = query(collection(db, 'leads'), orderBy('createdAt', 'desc'));
  if (filters.status) {
    q = query(collection(db, 'leads'), where('status', '==', filters.status), orderBy('createdAt', 'desc'));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Update lead status or any fields
export async function updateLead(id, fields) {
  const ref = doc(db, 'leads', id);
  await updateDoc(ref, { ...fields, updatedAt: serverTimestamp() });
}

// Delete a lead
export async function deleteLead(id) {
  await deleteDoc(doc(db, 'leads', id));
}
